import React, { useState, useEffect } from 'react';
import {
  History,
  MessageSquare,
  Plus,
  Loader2,
  Clock,
  Trash2,
  X
} from 'lucide-react';
import { collection,
  addDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  deleteDoc,
  doc } from '../firebase';
import { db, auth } from '../firebase';
import { notify } from '../services/notify';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { HistoryNote } from '../types';
import { MentionInput } from './ui/MentionInput';
import { createLocalNotifications } from '../services/notificationService';
import EntityActionLog from './EntityActionLog';

// Helper to safely format dates
const formatNoteDate = (val: any) => {
  if (!val) return 'Just now';
  try {
    if (val && typeof val === 'object' && val.toDate) {
      return val.toDate().toLocaleString();
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return 'Just now';
    return d.toLocaleString();
  } catch (e) {
    return 'Just now';
  }
};

// Error Info Interface for Firestore
enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: any;
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
}

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${getLocalAuthToken()}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

interface HistoryNotesProps {
  entityId: string;
  entityType: string;
  orgId: string;
  showActionLog?: boolean;
  showHeader?: boolean;
  locale?: 'en' | 'ar';
  compact?: boolean;
  className?: string;
}

const HISTORY_TEXT = {
  en: { heading: 'History & Notes', placeholder: 'Add a note or comment… use @ to mention', add: 'Add note', empty: 'No notes yet', delete: 'Delete note' },
  ar: { heading: 'السجل والملاحظات', placeholder: 'أضف ملاحظة أو تعليقًا… استخدم @ للإشارة', add: 'إضافة ملاحظة', empty: 'لا توجد ملاحظات بعد', delete: 'حذف الملاحظة' },
} as const;

export default function HistoryNotesView({ entityId, entityType, orgId, showActionLog = true, showHeader = true, locale = 'en', compact = false, className }: HistoryNotesProps) {
  const text = HISTORY_TEXT[locale];
  const [notes, setNotes] = useState<HistoryNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      void localApiRequest<any[]>(`/api/organizations/${orgId}/users`)
        .then(memberships => {
          if (cancelled) return;
          setUsers(memberships
            .filter(member => !member.suspended)
            .map(member => ({
              id: member.userId,
              display: member.displayName || member.email || 'Unnamed User'
            }))
          );
        })
        .catch(error => console.error('Local API error loading mention users:', error));
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'org_users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allUsers = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          display: data.fullName || data.userName || data.email || data.name || 'Unnamed User',
          memberships: data.memberships || [],
          orgId: data.orgId
        };
      });
      const filteredUsers = allUsers.filter(u =>
        u.orgId === orgId || u.memberships?.some((m: any) => m.orgId === orgId)
      );
      setUsers(filteredUsers);
    });
    return () => unsubscribe();
  }, [orgId]);

  const sendMentionNotifications = async (text: string, documentId: string) => {
    const localSession = isLocalSession();
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let match;
    const mentionedUserIds = new Set<string>();

    while ((match = mentionRegex.exec(text)) !== null) {
      const userId = match[2];
      if (userId !== auth.currentUser?.uid) {
        mentionedUserIds.add(userId);
      }
    }

    if (mentionedUserIds.size > 0 && localSession) {
      await createLocalNotifications(orgId, mentionedUserIds, {
        title: 'New Mention',
        message: `${auth.currentUser?.displayName || 'Someone'} mentioned you in a note on ${entityType}`,
        type: 'mention',
        relatedId: documentId,
      });
      return;
    }

    if (mentionedUserIds.size > 0) {
      const notificationsRef = collection(db, 'notifications');
      const notificationPromises = Array.from(mentionedUserIds).map(userId => {
        return addDoc(notificationsRef, {
          userId: userId,
          orgId,
          title: 'New Mention',
          message: `${auth.currentUser?.displayName || 'Someone'} mentioned you in a note on ${entityType}`,
          type: 'mention',
          relatedId: documentId,
          read: false,
          createdAt: serverTimestamp()
        });
      });
      await Promise.all(notificationPromises);
    }
  };

  useEffect(() => {
    if (!entityId || !orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setIsLoading(true);
      const parameters = new URLSearchParams({ entityId, entityType });
      void localApiRequest<HistoryNote[]>(`/api/organizations/${orgId}/history-notes?${parameters.toString()}`)
        .then(loadedNotes => {
          if (!cancelled) setNotes(loadedNotes.filter(note => String(note.type || '').toLowerCase() !== 'system'));
        })
        .catch(error => {
          console.error('Local API error loading history notes:', error);
          if (!cancelled) notify.error(error instanceof Error ? error.message : 'Failed to load history notes');
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'history_notes'),
      where('orgId', '==', orgId),
      where('entityId', '==', entityId),
      where('entityType', '==', entityType),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as HistoryNote[];
      setNotes(data.filter(note => String(note.type || '').toLowerCase() !== 'system'));
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching history notes:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [entityId, entityType, orgId]);

  const handleAddNote = async (e?: React.FormEvent) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newNote.trim()) return;

    setIsSaving(true);
    try {
      if (isLocalSession()) {
        const saved = await localApiRequest<HistoryNote>(`/api/organizations/${orgId}/history-notes`, {
          method: 'POST',
          body: JSON.stringify({ entityId, entityType, note: newNote })
        });
        setNotes(current => [saved, ...current]);
        await sendMentionNotifications(newNote, entityId);
      } else {
        await addDoc(collection(db, 'history_notes'), {
          entityId,
          entityType,
          orgId,
          note: newNote,
          type: 'Note',
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp(),
          userName: auth.currentUser?.displayName || auth.currentUser?.email || 'Unknown User'
        });
        await sendMentionNotifications(newNote, entityId);
      }
      setNewNote('');
      notify.confirm("Note added");
    } catch (error) {
      if (isLocalSession()) {
        console.error('Local API error adding history note:', error);
        notify.error(error instanceof Error ? error.message : 'Failed to add note');
      } else {
        handleFirestoreError(error, OperationType.CREATE, 'history_notes');
        console.error("Error adding note:", error);
        notify.error("Failed to add note");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (id: string) => {
    try {
      if (isLocalSession()) {
        await localApiRequest(`/api/organizations/${orgId}/history-notes/${id}`, { method: 'DELETE' });
        setNotes(current => current.filter(note => note.id !== id));
      } else {
        await deleteDoc(doc(db, 'history_notes', id));
      }
      notify.confirm("Note deleted");
    } catch (error) {
      if (isLocalSession()) {
        console.error('Local API error deleting history note:', error);
        notify.error(error instanceof Error ? error.message : 'Failed to delete note');
      } else {
        handleFirestoreError(error, OperationType.DELETE, `history_notes/${id}`);
        console.error("Error deleting note:", error);
        notify.error("Failed to delete note");
      }
    }
  };

  const renderTextWithMentions = (text: any) => {
    try {
      if (text === null || text === undefined) return null;
      const str = String(text);
      if (!str.trim()) return null;

      const parts = str.split(/(@\[[^\]]+\]\([^)]+\))/g);
      if (parts.length === 1) return str;

      return parts.map((part: string, i: number) => {
        const match = part.match(/@\[([^\]]+)\]\(([^)]+)\)/);
        if (match) {
          const display = match[1];
          const id = match[2];
          return (
            <span key={`${i}-${id}`} className="text-blue-600 font-bold bg-blue-50 px-1 rounded">
              @{display}
            </span>
          );
        }
        return part;
      });
    } catch (e) {
      console.error("Error rendering mentions:", e);
      return String(text || '');
    }
  };

  const formatAuditValue = (value: any) => {
    if (value === null) return 'None';
    if (value === undefined) return 'Not set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const getChangedFields = (note: HistoryNote) => {
    const fields = note.metadata?.changedFields;
    return Array.isArray(fields) ? fields : [];
  };

  return (
    <div className={cn(compact ? 'space-y-4' : 'space-y-6', className)} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      {showHeader && (
        <div className="flex items-center gap-2 border-b border-[#141414]/10 pb-4">
          <History className="w-5 h-5 opacity-40" />
          <h2 className="text-lg font-bold italic serif">{text.heading}</h2>
        </div>
      )}

      {/* Add Note Form */}
      <div className="space-y-4">
        <div className={cn('relative overflow-hidden border border-border bg-background/70 transition-all focus-within:bg-background focus-within:ring-2 focus-within:ring-ring/20', compact ? 'min-h-[84px] rounded-xl' : 'min-h-[100px] rounded-2xl')}>
          <MentionInput
            value={newNote}
            onChange={setNewNote}
            users={users}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddNote();
              }
            }}
            placeholder={text.placeholder}
            className={cn('w-full resize-none bg-transparent p-3 outline-none', compact ? 'min-h-[84px] pe-12 pb-10 text-sm' : 'min-h-[100px] p-4')}
          />
          <button
            type="button"
            onClick={handleAddNote}
            disabled={isSaving || !newNote.trim()}
            className={cn('absolute bottom-3 end-3 z-10 flex items-center gap-2 rounded-full bg-primary font-bold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-30', compact ? 'p-2.5' : 'px-6 py-2')}
            aria-label={text.add}
            title={text.add}
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {!compact && <span>{text.add}</span>}
          </button>
        </div>
      </div>

      {/* History List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 opacity-30">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-10 opacity-30 italic serif">
            {text.empty}
          </div>
        ) : (
          <div className="relative space-y-3 before:absolute before:bottom-2 before:start-4 before:top-2 before:w-px before:bg-border">
            {notes.map((note) => (
              <div key={note.id} className="group relative ps-10">
                <div className="absolute start-0 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background">
                  {note.type === 'System' ? <Clock className="w-4 h-4 opacity-40" /> : <MessageSquare className="w-4 h-4 opacity-40" />}
                </div>
                <div className={cn('border border-border bg-background/70 transition-all hover:border-foreground/30 group-hover:shadow-sm', compact ? 'rounded-xl p-3' : 'rounded-2xl p-4')}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-widest opacity-40">{note.userName}</span>
                      <span className="text-[10px] opacity-30">•</span>
                      <span className="text-[10px] opacity-30 font-bold uppercase tracking-widest">
                        {formatNoteDate(note.createdAt)}
                      </span>
                      {note.type === 'System' && (
                        <span className="rounded-full bg-[#141414] px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-white">
                          {note.action}
                        </span>
                      )}
                    </div>
                    {note.type === 'Note' && (
                      <button
                        type="button"
                        onClick={() => handleDeleteNote(note.id!)}
                        className="rounded p-1 text-red-500 opacity-60 transition-opacity hover:bg-red-50 group-hover:opacity-100"
                        aria-label={text.delete}
                        title={text.delete}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm font-bold leading-relaxed">{renderTextWithMentions(note.note)}</p>
                  {note.type === 'System' && getChangedFields(note).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {getChangedFields(note).map(field => (
                        <span key={field} className="rounded-full bg-blue-50 px-2 py-1 text-[9px] font-black uppercase tracking-wider text-blue-700">
                          {field.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                  {note.type === 'System' && (note.metadata?.beforeContact !== undefined || note.metadata?.afterContact !== undefined) && note.metadata?.beforeContact !== note.metadata?.afterContact && (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs">
                      <span className="font-black uppercase tracking-wider text-amber-700">Contact changed</span>
                      <div className="mt-1 font-medium text-amber-950">
                        {note.metadata?.beforeContact || 'None'} → {note.metadata?.afterContact || 'None'}
                      </div>
                    </div>
                  )}
                  {note.type === 'System' && (note.metadata?.before || note.metadata?.after) && (
                    <details className="mt-3 rounded-xl border border-[#141414]/10 bg-[#141414]/[0.03] p-3">
                      <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest opacity-60">
                        View old and new values
                      </summary>
                      <div className="mt-3 grid gap-3 md:grid-cols-2">
                        {note.metadata?.before && (
                          <div className="min-w-0">
                            <div className="mb-1 text-[9px] font-black uppercase tracking-widest text-red-600">Old</div>
                            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-red-50 p-3 text-[10px] text-red-950">{formatAuditValue(note.metadata.before)}</pre>
                          </div>
                        )}
                        {note.metadata?.after && (
                          <div className="min-w-0">
                            <div className="mb-1 text-[9px] font-black uppercase tracking-widest text-green-700">New</div>
                            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-green-50 p-3 text-[10px] text-green-950">{formatAuditValue(note.metadata.after)}</pre>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showActionLog && (
        <div className="border-t border-[#141414]/10 pt-6">
          <EntityActionLog orgId={orgId} entityId={entityId} entityType={entityType} locale={locale} compact={compact} />
        </div>
      )}
    </div>
  );
}
